import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageSquare } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Stars, StarInput } from "@/components/Stars";

const field = "w-full rounded-xl border border-border bg-card px-4 py-3 text-sm text-forest outline-none transition-colors focus:border-clay placeholder:text-muted-foreground";

export const Reviews = ({ productId, ratingAvg = 0, ratingCount = 0, onChange }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", rating: 0, comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get(`/products/${productId}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (user && !form.name) setForm((f) => ({ ...f, name: user.name }));
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [productId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please add your name");
    if (!form.rating) return toast.error("Please select a star rating");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/products/${productId}/reviews`, form);
      toast.success("Thanks for your review!");
      setForm({ name: user ? user.name : "", rating: 0, comment: "" });
      load();
      if (onChange) onChange(data.rating_avg, data.rating_count);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="reviews-section" className="mt-24">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-4xl font-semibold tracking-tight text-forest">Customer reviews</h2>
          {ratingCount > 0 ? (
            <div className="mt-2 flex items-center gap-2">
              <Stars value={ratingAvg} size="h-5 w-5" />
              <span className="text-sm font-medium text-forest">{ratingAvg.toFixed(1)}</span>
              <span className="text-sm text-muted-foreground">· {ratingCount} review{ratingCount !== 1 ? "s" : ""}</span>
            </div>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">Be the first to review this product.</p>
          )}
        </div>
      </div>

      <div className="mt-8 grid gap-10 lg:grid-cols-5">
        {/* Form */}
        <form onSubmit={submit} className="lg:col-span-2 h-fit rounded-[1.5rem] border border-border/70 bg-white p-6" data-testid="review-form">
          <h3 className="font-display text-2xl font-semibold text-forest">Write a review</h3>
          <div className="mt-4 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Your rating</label>
              <StarInput value={form.rating} onChange={(r) => setForm({ ...form, rating: r })} />
            </div>
            <input data-testid="review-name" className={field} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <textarea data-testid="review-comment" className={field} rows={4} placeholder="Share your experience…" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
            <button data-testid="review-submit" disabled={submitting} className="w-full rounded-full bg-clay py-3 text-sm font-semibold text-white transition-colors hover:bg-[#A3161F] disabled:opacity-60">
              {submitting ? "Submitting…" : "Submit review"}
            </button>
          </div>
        </form>

        {/* List */}
        <div className="lg:col-span-3 space-y-4">
          {reviews.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center rounded-2xl border border-dashed border-border p-10 text-center">
              <MessageSquare className="h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">No reviews yet — your voice could be the first.</p>
            </div>
          ) : (
            reviews.map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                data-testid={`review-${r.id}`}
                className="rounded-[1.5rem] border border-border/70 bg-white p-5"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-clay/10 font-display text-lg font-semibold text-clay">
                      {r.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-forest">{r.name}</p>
                      <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })}</p>
                    </div>
                  </div>
                  <Stars value={r.rating} />
                </div>
                {r.comment && <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{r.comment}</p>}
              </motion.div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
